import { Capacitor } from '@capacitor/core';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from './firebase';
import { enablePush as enableWebPush, isPushConfigured } from './push';

/**
 * Service de notifications Push unifié.
 *
 *  - Sur Android/iOS natif (Capacitor) : utilise @capacitor/push-notifications.
 *    Demande la permission, enregistre l'appareil auprès de FCM/APNs et stocke
 *    le jeton dans users/{userId}.fcmTokens.
 *  - Sur le web : délègue à enableWebPush() (lib/push.js, FCM Web + VAPID).
 *
 * Toutes les fonctions sont sûres à appeler sur n'importe quelle plateforme.
 */

const isNative = Capacitor.isNativePlatform();

/**
 * ⚠️ Notifications push natives (Android/iOS) — DÉSACTIVÉ tant que Firebase
 * Cloud Messaging n'est pas configuré côté natif.
 *
 * Sans `android/app/google-services.json` (et la config APNs iOS), l'appel à
 * `PushNotifications.register()` lève une exception native
 * « Default FirebaseApp is not initialized » qui N'EST PAS rattrapable par le
 * try/catch JavaScript : l'application se ferme brutalement au montage du
 * tableau de bord, juste après la connexion.
 *
 * Pour réactiver le push natif :
 *   1. Console Firebase → ajouter une app Android « com.daara.wakanahaqqan ».
 *   2. Télécharger google-services.json → le placer dans android/app/.
 *   3. (iOS) ajouter GoogleService-Info.plist + activer Push Notifications.
 *   4. Repasser ce drapeau à `true`, puis `npx cap sync` et reconstruire.
 */
const NATIVE_PUSH_CONFIGURED = false;

/** Stocke un jeton d'appareil dans le profil Firestore de l'utilisateur. */
const saveToken = async (userId, token) => {
  if (!userId || !token) return;
  try {
    await updateDoc(doc(db, 'users', userId), { fcmTokens: arrayUnion(token) });
  } catch (e) {
    console.error('Impossible d’enregistrer le jeton push:', e?.message);
  }
};

let _listenersAttached = false;

/**
 * Enregistre les notifications push pour l'utilisateur connecté.
 * @param {string} userId  uid Firestore de l'utilisateur.
 * @param {(notif:any)=>void} [onReceived]  callback à la réception (app ouverte).
 * @param {(action:any)=>void} [onAction]    callback au tap sur la notification.
 * @returns {Promise<{ok:boolean, platform:string, token?:string, reason?:string}>}
 */
export const registerPush = async (userId, onReceived, onAction) => {
  if (isNative) {
    // Tant que FCM/APNs n'est pas configuré nativement, on n'appelle JAMAIS
    // register() : cet appel ferait planter l'app (voir NATIVE_PUSH_CONFIGURED).
    if (!NATIVE_PUSH_CONFIGURED) {
      return { ok: false, platform: 'native', reason: 'fcm_not_configured' };
    }
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');

      let perm = await PushNotifications.checkPermissions();
      if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
        perm = await PushNotifications.requestPermissions();
      }
      if (perm.receive !== 'granted') {
        return { ok: false, platform: 'native', reason: 'permission_denied' };
      }

      // Branche les écouteurs une seule fois.
      if (!_listenersAttached) {
        _listenersAttached = true;
        PushNotifications.addListener('registration', (tokenData) => {
          saveToken(userId, tokenData.value);
        });
        PushNotifications.addListener('registrationError', (err) => {
          console.error('Erreur d’enregistrement push:', err?.error);
        });
        PushNotifications.addListener('pushNotificationReceived', (notif) => {
          onReceived?.(notif);
        });
        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          onAction?.(action);
        });
      }

      await PushNotifications.register();
      return { ok: true, platform: 'native' };
    } catch (e) {
      console.error('registerPush natif a échoué:', e?.message);
      return { ok: false, platform: 'native', reason: e?.message };
    }
  }

  // --- Web ---
  if (!isPushConfigured()) {
    return { ok: false, platform: 'web', reason: 'vapid_missing' };
  }
  try {
    const token = await enableWebPush(userId);
    return { ok: true, platform: 'web', token };
  } catch (e) {
    return { ok: false, platform: 'web', reason: e?.message };
  }
};

/** Supprime toutes les notifications affichées (badge/cleanup). */
export const clearDeliveredPush = async () => {
  if (!isNative || !NATIVE_PUSH_CONFIGURED) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.removeAllDeliveredNotifications();
  } catch { /* no-op */ }
};

/** Indique si le push est disponible sur la plateforme courante. */
export const isPushAvailable = () => (isNative ? NATIVE_PUSH_CONFIGURED : isPushConfigured());
